import { Observable, ReplaySubject, Subject } from 'rxjs';
import { loading } from './LoadingService';
import { ngb } from '@nickalie/ngb';
import { APP_ID, getQueryParam, IConfig, IMainboardState, IPost, SocialPlatform } from '../../../common';
import { isEmptyString } from '@nickalie/ngb/utils';
import Swal from 'sweetalert2';
import { fileService } from './FileService';

class APIService {
  private readonly _subjectState: Subject<IMainboardState> = new ReplaySubject(1);
  private readonly _subjectConfig: Subject<IConfig> = new ReplaySubject(1);
  private readonly _subjectAuth: Subject<boolean> = new ReplaySubject(1);

  public readonly state: Observable<IMainboardState> = this._subjectState;
  public readonly config: Observable<IConfig> = this._subjectConfig;
  public readonly auth: Observable<boolean> = this._subjectAuth;

  private _state: IMainboardState = { posts: [] };

  public async login(): Promise<boolean> {
    let code = getQueryParam('c');

    if (isEmptyString(code)) {
      code = localStorage.getItem(`${APP_ID}.code`) as string;

      if (isEmptyString(code)) {
        const result = await Swal.fire<any>({
          icon: 'info',
          title: 'ВВЕДИТЕ КОД',
          text: 'После активации приложениe будет доступно в течение оплаченного периода',
          input: 'text',
          allowOutsideClick: false,
          allowEnterKey: false,
          allowEscapeKey: false,
          confirmButtonText: 'АКТИВИРОВАТЬ',
          preConfirm: value => {
            if (isEmptyString(value)) {
              Swal.showValidationMessage('НЕВЕРНЫЙ КОД');
            }
          },
        });

        code = result.value;
        localStorage.setItem(`${APP_ID}.code`, code);
      }
    }

    if (code === 'ZGVtbw') {
      code = await loading.wrap(ngb.api.call('demo-sw'));
    }

    code = atob(code);
    const [cid, uid] = code.split(':');

    try {
      await loading.wrap(
        ngb.init({
          cid: cid,
          aid: APP_ID,
          admin: true,
        }),
      );
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'СРОК ДЕЙСТВИЯ КОДА ИСТЁК',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEnterKey: false,
        allowEscapeKey: false,
      });
      localStorage.removeItem(`${APP_ID}.code`);
      return false;
    }

    const isLoggedIn = await loading.wrap(ngb.auth.isLoggedIn());

    if (!isLoggedIn) {
      await loading.wrap(ngb.auth.login(uid));
    }

    const isAdmin = await loading.wrap(ngb.auth.isAdmin());

    if (!isAdmin) {
      return false;
    }

    ngb.state.watch(this.stateHandler.bind(this));
    ngb.config.watch<IConfig>((value: IConfig) => this._subjectConfig.next(value));
    this._subjectAuth.next(true);
    return true;
  }

  public async activatePosts(values: IPost | IPost[]): Promise<void> {
    if (!Array.isArray(values)) {
      values = [values];
    }

    let needUpdate = false;

    for (const value of values) {
      if (this._state.posts.find(item => item.id === value.id)) {
        continue;
      }

      value.activated = true;
      this._state.posts.push(value);
      needUpdate = true;
    }

    if (needUpdate) {
      return ngb.state.update({
        posts: this._state.posts,
      });
    }
  }

  public async deactivatePosts(values: IPost | IPost[]): Promise<void> {
    if (!Array.isArray(values)) {
      values = [values];
    }

    for (const value of values) {
      const index = this._state.posts.findIndex(item => item.id === value.id);

      if (index === -1) {
        continue;
      }

      value.activated = false;
      this._state.posts.splice(index, 1);

      if (value.platform == SocialPlatform.CUSTOM) {
        fileService.delete(value.id);
      }
    }

    return ngb.state.update({
      posts: this._state.posts,
    });
  }

  public clearPosts(): void {
    this._state.posts.forEach(post => {
      if (post.platform === SocialPlatform.CUSTOM) {
        fileService.delete(post.id);
      }
    });

    this._state.posts = [];

    loading.wrap(
      ngb.state.update({
        posts: this._state.posts,
      }),
    );
  }

  public toggleAuto(auto: boolean): void {
    loading.wrap(ngb.config.update({ auto }));
  }

  public updateBackground(background: string): void {
    loading.wrap(ngb.config.update({ background }));
  }

  public updateHashtag(hashtag: string): void {
    if (hashtag == null) {
      return;
    }

    ngb.config.update({ hashtag });
  }

  public getMainboardURL(): string {
    return `https://sw.ngflex.com/?c=${btoa(ngb.cid)}`;
  }

  private stateHandler(value?: IMainboardState): void {
    if (!value) {
      value = { posts: [] };
    }

    if (!value.posts) {
      value.posts = [];
    }

    this._state = value;

    this._subjectState.next(this._state);
  }

  public get cid(): string {
    return ngb.cid;
  }
}

export const api: APIService = new APIService();
